import { useEffect, useState } from 'react';
import { isApiError } from '@aiptyg/types';
import type { MessagePoolStatsDTO } from '../types/tasks.types';
import { getMessagePoolStats, setMessageKillSwitch, suppressMessageItem } from '../services/tasks.service';

interface MessagePoolStatsProps {
  taskId: number;
  refreshKey?: number;
  onStats?: (stats: MessagePoolStatsDTO | null) => void;
  canSuppress?: boolean;
  showKillSwitch?: boolean;
}

const ZERO_STATS: MessagePoolStatsDTO = {
  available: 0,
  assigned: 0,
  sent: 0,
  reviewed: 0,
  suppressed: 0,
};

function resolveError(e: unknown): string {
  if (isApiError(e)) return e.message;
  if (e && typeof e === 'object' && 'message' in e && typeof e.message === 'string') return e.message;
  return '操作失败';
}

export function MessagePoolStats({ taskId, refreshKey = 0, onStats, canSuppress = true, showKillSwitch = true }: MessagePoolStatsProps) {
  const [stats, setStats] = useState<MessagePoolStatsDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [itemId, setItemId] = useState('');
  const [reason, setReason] = useState('');
  const [killSwitchReason, setKillSwitchReason] = useState('');
  const [acting, setActing] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const next = await getMessagePoolStats(taskId);
      setStats(next);
      onStats?.(next);
    } catch (e) {
      setStats(null);
      onStats?.(null);
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [taskId, refreshKey]);

  const visibleStats = stats ?? ZERO_STATS;
  const statItems = [
    ['available', '可领取', visibleStats.available],
    ['assigned', '已分配', visibleStats.assigned],
    ['sent', '已发送遥测', visibleStats.sent],
    ['reviewed', '已审核', visibleStats.reviewed],
    ['suppressed', '已抑制', visibleStats.suppressed],
  ] as const;

  const handleSuppress = async () => {
    if (!itemId.trim() || !reason.trim()) return;
    setActing(true);
    setError('');
    try {
      const next = await suppressMessageItem(taskId, itemId.trim(), reason.trim());
      setStats(next);
      onStats?.(next);
      setItemId('');
      setReason('');
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setActing(false);
    }
  };

  const handleKillSwitch = async (enabled: boolean) => {
    if (!killSwitchReason.trim()) return;
    setActing(true);
    setError('');
    try {
      await setMessageKillSwitch(enabled, killSwitchReason.trim());
      await load();
      setKillSwitchReason('');
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setActing(false);
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-medium text-gray-900">收件人池状态</h2>
          <p className="mt-1 text-xs text-gray-500">已发送遥测出现后，相关快照和配置应保持审计一致。</p>
        </div>
        <button onClick={load} disabled={loading} className="h-9 rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40">
          {loading ? '刷新中...' : '刷新'}
        </button>
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <div className="grid gap-2 sm:grid-cols-5">
        {statItems.map(([key, label, value]) => (
          <div key={key} className="rounded-md bg-gray-50 px-3 py-2">
            <div className="text-xs text-gray-500">{label}</div>
            <div className="mt-1 font-mono text-lg tabular-nums text-gray-900">{value}</div>
          </div>
        ))}
      </div>

      {(canSuppress || showKillSwitch) && <div className={`grid gap-4 ${canSuppress && showKillSwitch ? 'lg:grid-cols-2' : ''}`}>
        {canSuppress && <div className="space-y-3 rounded-md border border-gray-200 p-4">
          <div>
            <h3 className="text-sm font-medium text-gray-900">抑制收件人池条目</h3>
            <p className="mt-1 text-xs text-gray-500">输入服务端 item ID 和审计原因；抑制判定由后端执行。</p>
          </div>
          <input value={itemId} onChange={(e) => setItemId(e.target.value)} placeholder="Item ID" className="h-9 w-full rounded-md border border-gray-300 px-3 font-mono text-xs tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50" />
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="抑制原因" className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" />
          <button onClick={handleSuppress} disabled={!itemId.trim() || !reason.trim() || acting} className="h-9 rounded-md border border-red-300 px-3 text-sm text-red-600 hover:bg-red-50 disabled:opacity-40">
            抑制条目
          </button>
        </div>}

        {showKillSwitch && <div className="space-y-3 rounded-md border border-gray-200 p-4">
          <div>
            <h3 className="text-sm font-medium text-gray-900">全局 Kill Switch</h3>
            <p className="mt-1 text-xs text-gray-500">开启后会阻断新的领取、会话、证据意图和审核外呼；恢复前需确认队列与合规状态。</p>
          </div>
          <div className="text-sm text-gray-700">
            当前状态：
            <span className={visibleStats.kill_switch_enabled ? 'text-red-600' : 'text-green-700'}>
              {visibleStats.kill_switch_enabled ? '已开启' : '未开启或未返回'}
            </span>
          </div>
          <input value={killSwitchReason} onChange={(e) => setKillSwitchReason(e.target.value)} placeholder="操作审计原因" className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => void handleKillSwitch(true)} disabled={!killSwitchReason.trim() || acting} className="h-9 rounded-md border border-red-300 px-3 text-sm text-red-600 hover:bg-red-50 disabled:opacity-40">
              开启
            </button>
            <button onClick={() => void handleKillSwitch(false)} disabled={!killSwitchReason.trim() || acting} className="h-9 rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40">
              恢复
            </button>
          </div>
        </div>}
      </div>}
    </section>
  );
}
